/**
 * @typedef {Object} Region
 * @property {string} name
 * @property {string} description
 */

import { Region } from "../types";

// These characters are not visually distinguishable in SKU/entitlement values,
// but they change string equality and can be persisted by the API/database.
const INVISIBLE_UNICODE_CHARACTERS = /[\u00A0\u00AD\u034F\u061C\u115F\u1160\u17B4\u17B5\u180B-\u180D\u180E\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u206F\u2800\u3000\u3164\uFEFF]/g;

/** Remove Unicode formatting/separator characters that should not be stored in identifiers or labels. */
export function sanitizeTextInput(value: string): string {
  return value.replace(INVISIBLE_UNICODE_CHARACTERS, '');
}

/** Return whether a string contains a disallowed invisible Unicode character. */
export function containsInvisibleCharacters(value: string): boolean {
  return sanitizeTextInput(value) !== value;
}

/**
 * Ant Design Form validator — rejects invisible Unicode characters and values
 * that start or end with whitespace. Add to any Form.Item rules array wrapping
 * an <Input> or <TextArea>.
 */
export const noLeadingTrailingSpaces = {
  validator(_: unknown, value: unknown) {
    if (typeof value !== 'string' || value.length === 0) {
      return Promise.resolve();
    }
    if (containsInvisibleCharacters(value)) {
      return Promise.reject(new Error('Must not contain invisible characters'));
    }
    if (value !== value.trim()) {
      return Promise.reject(new Error('Must not start or end with whitespace'));
    }
    return Promise.resolve();
  },
};

/**
 * @typedef {Object} RegionNode
 * @property {string} name
 * @property {string} description
 * @property {RegionNode[]} [children]
 */

/**
 * Extract all regions from the flat hierarchy response
 * @param {Record<string, unknown>} response - API response with data array
 * @returns {Region[]}
 */
export function extractAllRegions(response: Record<string, unknown>): Region[] {
  // Check if response has data array
  if (!response || !response.data || !Array.isArray(response.data)) {
    return [];
  }

  // Return the flat list of regions, sorted by flat_priority
  return response.data.sort((a: Record<string, number>, b: Record<string, number>) => a.flat_priority - b.flat_priority);
}

/**
 * Format an entitlement for display in dropdown options.
 * Output: "(description) name - value - needs_quantity - unit"
 * "(description) " is omitted when description is empty/null.
 * "unit" segment is omitted when unit is empty/null.
 */
export function formatEntitlementLabel(ent: {
  name: string;
  value: string;
  description?: string | null;
  needs_quantity?: boolean;
  unit?: string | null;
}): string {
  const desc = ent.description?.trim() ? `(${ent.description.trim()}) ` : '';
  const qty = ent.needs_quantity !== undefined ? ` - ${ent.needs_quantity ? 'Yes' : 'No'}` : '';
  const unit = ent.unit?.trim() ? ` - ${ent.unit.trim()}` : '';
  return `${desc}${ent.name} - ${ent.value}${qty}${unit}`;
}

/**
 * Extract only leaf regions (tier 3 regions - the most specific geographical regions)
 * @param {Record<string, unknown>} response - API response with data array
 * @returns {Region[]}
 */
export function extractLeafRegions(response: Record<string, unknown>): Region[] {
  // Check if response has data array
  if (!response || !response.data || !Array.isArray(response.data)) {
    return [];
  }

  // Filter for leaf regions (tier 3 - highest tier number are the leaf nodes)
  // Sort by flat_priority
  return response.data
    .filter((region: Record<string, unknown>) => region.tier === 3)
    .sort((a: Record<string, number>, b: Record<string, number>) => a.flat_priority - b.flat_priority);
}
